'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { PhoneMissed, Thermometer } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'
import { ResultStamp, Thread, Trail } from './demoParts'

// HVAC: the page's July scenario. A homeowner's call is missed while the crew
// is on a job; GoHighLevel texts back in 42 seconds with the page's own
// message, takes their zip code, offers a tech window and books it, while
// the competitor they also called is still going to voicemail.

const DELAYS = [600, 1300, 1400, 1200, 1300, 1100, 700]
const SCRIPT = [
  { at: 2, from: 'us', meta: 'Auto text-back · 42 s after the missed call', text: 'Hi, we missed your call. Reply with your zip code and we will send a tech window.' },
  { at: 3, from: 'them', text: '85032. AC stopped blowing cold air.' },
  { at: 4, from: 'us', text: 'Thanks! A tech can be there today between 3 and 5 PM. Reply YES to book.' },
  { at: 5, from: 'them', text: 'YES' },
]

export default function MissedCall() {
  const [ref, onScreen] = useOnScreen()
  const [run, setRun] = useState(0)
  const step = useSequence(DELAYS, { run: onScreen, resetKey: run })
  const finished = step >= DELAYS.length
  const messages = SCRIPT.filter((m) => step >= m.at)

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-hvac"
      title="A missed call, still booked"
      label="Demo: an HVAC missed call answered by an automatic text-back and booked"
      onReplay={() => setRun((n) => n + 1)}
    >
      <div className="svd-hvac-top">
        <div className={`svd-missed${step >= 1 ? ' is-missed' : ' is-ringing'}`}>
          <span className="svd-missed-icon" aria-hidden="true"><PhoneMissed size={18} /></span>
          <span>
            <strong>{step >= 1 ? 'Missed call · crew on a job' : 'Incoming call…'}</strong>
            <small>(602) 555-0193 · 2:47 PM</small>
          </span>
          <span className="svd-chip is-wait"><Thermometer size={12} aria-hidden="true" /> July · 97°F</span>
        </div>
        <motion.div className="svd-big-stat" initial={{ opacity: 0, scale: 0.8 }} animate={step >= 2 ? { opacity: 1, scale: 1 } : { opacity: 0.25, scale: 0.92 }}>
          <span className="svd-3d-num is-small">42s</span>
          <small>to text back</small>
        </motion.div>
      </div>
      <div className="svd-agent-grid">
        <Thread messages={messages} id={`hvac-${run}`} />
        <Trail
          title="In GoHighLevel"
          items={[
            { at: 1, text: 'Missed call detected' },
            { at: 2, text: 'Text-back sent in 42 seconds' },
            { at: 3, text: 'Zip code 85032 captured' },
            { at: 4, text: 'Tagged as a hot lead' },
            { at: 6, text: 'Estimate booked: today 3–5 PM' },
          ]}
          step={step}
        />
      </div>
      <p className={`svd-competitor${step >= 3 ? ' is-on' : ''}`}>Meanwhile, the other contractor they called: <b>still ringing to voicemail</b>.</p>
      <ResultStamp show={finished} tone="good" text="Booked before the competitor called back" id={`hvac-${run}`} />
    </DemoFrame>
  )
}
